/**
 * One sidebar navigation row.
 *
 * Styles verbatim from the prototype, including the 0.55 opacity on locked items
 * and the `lyx-nav-label` class that the responsive stylesheet uses to hide labels
 * when the sidebar collapses.
 *
 * Navigation is by Link now rather than by setting a view string, so each item is
 * a real link with an href: middle-click and open-in-new-tab work, and the active
 * state is derived from the URL rather than from component state.
 */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Lock } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { c } from '../../styles/theme';

export const NAV_ROW_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 10px',
  borderRadius: 8,
  border: 'none',
  width: '100%',
  cursor: 'pointer',
  textAlign: 'left' as const,
  boxSizing: 'border-box' as const,
};

export function NavRowContent({
  icon: Icon,
  label,
  active = false,
  locked = false,
  visualLocked = false,
  comingSoon = false,
  trailing,
}: {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  locked?: boolean;
  visualLocked?: boolean;
  comingSoon?: boolean;
  trailing?: ReactNode;
}) {
  const muted = locked || visualLocked ? 0.55 : comingSoon ? 0.78 : 1;
  const iconColor = active ? c.accent : comingSoon ? c.text3 : c.text2;
  const labelColor = active ? c.text1 : comingSoon ? c.text3 : c.text2;

  return (
    <>
      <Icon size={16} color={iconColor} style={{ opacity: muted, flexShrink: 0 }} />
      <span
        className="lyx-nav-label"
        style={{
          fontSize: 13,
          color: labelColor,
          fontFamily: 'Inter',
          opacity: muted,
          flex: 1,
        }}
      >
        {label}
      </span>
      {/* The padlock is the only thing distinguishing a gated row, so it carries
          meaning and takes text2. */}
      {locked && <Lock size={11} color={c.text2} className="lyx-nav-label" />}
      {trailing && !locked ? (
        <span className="lyx-nav-label" style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
          {trailing}
        </span>
      ) : null}
    </>
  );
}

export interface NavItemProps {
  icon: LucideIcon;
  label: string;
  /** Destination path. */
  to: string;
  /**
   * Extra path prefixes that should also light this item up — e.g. Settings stays
   * active on /upgrade and /topup, which are reached from it but have no sidebar
   * row of their own.
   */
  alsoActiveOn?: string[];
  /**
   * Matches `to` exactly, so deeper paths under it do NOT light this row up.
   *
   * The Library needs it. `/library/:songId` is the Studio workspace, which has a row
   * of its own — under the default prefix rule both rows would be highlighted while
   * you work on a track, and the highlight is the answer to "where am I?".
   *
   * `alsoActiveOn` still applies: exactness is about the row's own path, not about
   * the extra paths it has been told to claim.
   */
  exact?: boolean;
  /**
   * Shows the padlock and dims the row. Does not block navigation, matching the
   * prototype: a locked destination renders its own upgrade prompt.
   */
  locked?: boolean;
  /**
   * The same dimmed treatment, but without changing the accessible name.
   *
   * Used for rows that should LOOK gated in the sidebar while still remaining ordinary
   * destinations semantically — a teaser page is still a page, and calling it "locked"
   * to assistive technology would be saying a stronger thing than the route itself does.
   */
  visualLocked?: boolean;
  /**
   * Muted teaser styling for an upcoming feature.
   *
   * Unlike `locked`, this keeps the normal accessible name and shows no padlock: the row is
   * a real destination, only styled as "coming soon" rather than as an entitlement gate.
   */
  comingSoon?: boolean;
  /**
   * Whether a teaser row should also get the inset border + tinted idle background.
   *
   * Voice Cloner keeps the muted label/icon treatment but now sits flush with the rest of the
   * nav list, so its teaser styling can opt out of that extra chrome while preserving the same
   * active-route highlight and accessible name.
   */
  comingSoonChrome?: boolean;
  /** Extra element at the trailing edge of the row. */
  trailing?: ReactNode;
  /** Extra click handler, for items that also expand a submenu. */
  onClick?: () => void;
}

/**
 * One sidebar action row.
 *
 * Same geometry and responsive behaviour as NavItem, but a real button rather than a
 * destination link. Use this for things that DO something in-place, such as logging out,
 * without pretending they are routes.
 */
export interface NavActionItemProps {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
}

export function NavActionItem({ icon: Icon, label, onClick }: NavActionItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        ...NAV_ROW_STYLE,
        background: 'transparent',
      }}
    >
      <NavRowContent icon={Icon} label={label} />
    </button>
  );
}

/** A sidebar disclosure row: button semantics, nav-row styling, optional active state. */
export interface NavDisclosureItemProps {
  icon: LucideIcon;
  label: string;
  open: boolean;
  active?: boolean;
  controls?: string;
  onClick?: () => void;
  trailing?: ReactNode;
}

export function NavDisclosureItem({
  icon: Icon,
  label,
  open,
  active = false,
  controls,
  onClick,
  trailing,
}: NavDisclosureItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={open ? `Collapse ${label} menu` : `Expand ${label} menu`}
      aria-expanded={open}
      aria-controls={controls}
      title={label}
      style={{
        ...NAV_ROW_STYLE,
        backgroundColor: active ? c.surface2 : 'transparent',
      }}
    >
      <NavRowContent icon={Icon} label={label} active={active} trailing={trailing} />
    </button>
  );
}

export default function NavItem({
  icon: Icon,
  label,
  to,
  alsoActiveOn = [],
  exact,
  locked,
  visualLocked,
  comingSoon,
  comingSoonChrome = true,
  trailing,
  onClick,
}: NavItemProps) {
  // useLocation rather than window.location: the latter is not reactive, so the
  // active highlight would lag a navigation until some other state changed.
  const { pathname } = useLocation();
  const active =
    pathname === to ||
    (!exact && pathname.startsWith(`${to}/`)) ||
    alsoActiveOn.some((p) => pathname.startsWith(p));

  return (
    <Link
      to={to}
      onClick={onClick}
      aria-label={locked ? `${label} (locked)` : label}
      aria-current={active ? 'page' : undefined}
      title={locked ? `${label} — locked` : label}
      style={{
        ...NAV_ROW_STYLE,
        backgroundColor: active ? c.surface2 : comingSoon && comingSoonChrome ? c.surface1 : 'transparent',
        boxShadow:
          comingSoon && comingSoonChrome && !active ? `0 0 0 1px ${c.border} inset` : undefined,
        textDecoration: 'none',
      }}
    >
      <NavRowContent
        icon={Icon}
        label={label}
        active={active}
        locked={locked}
        visualLocked={visualLocked}
        comingSoon={comingSoon}
        trailing={trailing}
      />
    </Link>
  );
}
